import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError, downloadJournalPdf } from "../lib/api";
import { Sparkle, PaperPlaneTilt, ArrowsClockwise, DownloadSimple } from "@phosphor-icons/react";

export default function Journal() {
  const [today, setToday] = useState(null);
  const [content, setContent] = useState("");
  const [entries, setEntries] = useState([]);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [reflection, setReflection] = useState("");
  const [reflectionLoading, setReflectionLoading] = useState(false);

  const loadData = async () => {
    try {
      const [t, e] = await Promise.all([api.get("/journey/today"), api.get("/journal")]);
      setToday(t.data);
      setEntries(e.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  useEffect(() => { loadData(); }, []);

  const refreshPrompt = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post("/journey/ai-prompt");
      setToday((t) => ({ ...t, ai_prompt: data.prompt }));
      toast.success("A prompt just for you.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't generate prompt.");
    } finally { setAiLoading(false); }
  };

  const save = async () => {
    if (!content.trim()) return;
    setSaving(true);
    try {
      await api.post("/journal", { content, day_number: today.day });
      setContent("");
      toast.success("Entry saved.");
      loadData();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const getReflection = async () => {
    setReflectionLoading(true);
    try {
      const { data } = await api.post("/journal/reflection");
      setReflection(data.reflection);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setReflectionLoading(false); }
  };

  if (!today) return <div className="h-32" />;

  return (
    <div className="space-y-10">
      <div>
        <p className="text-xs tracking-[0.3em] uppercase text-sage font-bold mb-2">Day {today.day} of 90</p>
        <h1 className="font-serif text-4xl md:text-5xl font-medium mb-6">Today's reflection.</h1>

        <div className="relative bg-white rounded-3xl border border-border p-8 md:p-10 soft-shadow overflow-hidden">
          <div className="absolute -top-12 -right-12 w-56 h-56 rounded-full bg-clay/15 blur-3xl" />
          <div className="relative">
            <div className="flex items-start justify-between gap-4 mb-6">
              <p className="font-serif text-2xl md:text-3xl leading-snug max-w-3xl" data-testid="journal-prompt">
                {today.ai_prompt || today.prompt}
              </p>
              <button onClick={refreshPrompt} disabled={aiLoading} data-testid="journal-ai-prompt"
                      className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border hover:bg-cream text-sm">
                {aiLoading ? <ArrowsClockwise size={14} className="animate-spin"/> : <Sparkle size={14} weight="duotone" />}
                AI prompt
              </button>
            </div>
            <textarea value={content} onChange={(e) => setContent(e.target.value)}
                      data-testid="journal-textarea"
                      placeholder="Write freely. No one will see this but you."
                      className="w-full min-h-[260px] resize-y rounded-2xl border border-border bg-sand p-5 text-base leading-relaxed focus:outline-none focus:ring-2 focus:ring-sage transition-all" />
            <div className="flex items-center justify-between mt-4">
              <p className="text-xs text-muted-foreground">{content.length} characters</p>
              <button onClick={save} disabled={saving || !content.trim()} data-testid="journal-save"
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-moss text-white hover:bg-mossDark disabled:opacity-50 transition-all">
                <PaperPlaneTilt size={16} /> {saving ? "Saving…" : "Save entry"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* AI reflection */}
      <div className="bg-cream/50 rounded-3xl border border-border p-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-2xl">A reflection from your guide</h3>
          <button onClick={getReflection} disabled={reflectionLoading}
                  data-testid="journal-reflection-btn"
                  className="text-sm px-4 py-2 rounded-full bg-white border border-border hover:bg-cream">
            {reflectionLoading ? "Reflecting…" : "Reflect on my entries"}
          </button>
        </div>
        <p className="text-muted-foreground leading-relaxed italic" data-testid="journal-reflection-text">
          {reflection || "Tap reflect to receive a gentle observation from your AI journaling companion."}
        </p>
      </div>

      {/* Past entries */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-2xl">Past entries</h3>
          {entries.length > 0 && (
            <button onClick={() => downloadJournalPdf("journey90")} data-testid="journal-export-pdf"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border bg-white hover:bg-cream text-sm">
              <DownloadSimple size={14}/> Export PDF
            </button>
          )}
        </div>
        {entries.length === 0 ? (
          <p className="text-muted-foreground">Your entries will appear here.</p>
        ) : (
          <div className="space-y-3">
            {entries.map((e) => (
              <div key={e.id} data-testid={`entry-${e.id}`}
                   className="bg-white rounded-2xl border border-border p-5 soft-shadow">
                <p className="text-xs tracking-[0.2em] uppercase text-sage font-bold mb-2">
                  Day {e.day_number} · {new Date(e.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                </p>
                <p className="text-charcoal whitespace-pre-wrap leading-relaxed">{e.content}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
